import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { BenefitItem, MonthlyBenefit, BenefitsBreakdown } from "../utils/types";
import CustomCheckbox from "../components/CustomCheckbox";
import backArrowIcon from "../assets/icons/back-arrow-icon.svg";
import editIcon from "../assets/icons/edit-icon.svg";

const BenefitsSummary: React.FC = () => {
  const navigate = useNavigate();
  const { benefitsBreakdown, benefitsSummary, appliedBenefits } = useUser();
  const { setIsVisible } = useBottomNavBar();

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  const sectionHeaderMapping: { [key: string]: string } = {
    month: "Monthly benefits",
    energySupportSocialTariffs: "Energy support and social tariffs",
    discretionary: "Discretionary support",
    lumpSum: "Lump-sum",
    homeImprovementsGrants: "Home improvements grants",
    other: "Other support",
  };

  const isMonthlyBenefit = (
    item: BenefitItem | MonthlyBenefit,
  ): item is MonthlyBenefit => {
    return (
      "amount" in item && typeof (item as MonthlyBenefit).amount === "number"
    );
  };

  const handleBenefitClick = (item: BenefitItem | MonthlyBenefit) => {
    navigate(`/benefit-detail/${item.id}`);
  };

  const renderBenefitListItem = (item: BenefitItem | MonthlyBenefit) => {
    return (
      <div
        key={item.id}
        className="bg-white rounded-8 px-3 py-5 mb-1.5 cursor-pointer"
        onClick={() => handleBenefitClick(item)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <CustomCheckbox
              checked={appliedBenefits.includes(item.id)}
              onChange={() => {}} // Read-only - users must use detail page to toggle
              id={`benefit-${item.id}`}
              className="mr-2"
            />
            <span className="font-bold text-sm mr-2">{item.title}</span>
          </div>
          <div className="flex items-center">
            {isMonthlyBenefit(item) && (
              <span className="text-sm text-grey-80 mr-2">
                £{item.amount.toFixed(2)}
              </span>
            )}
            <img
              src={backArrowIcon}
              alt="arrow"
              className="w-5 h-5 transform rotate-180"
            />
          </div>
        </div>
      </div>
    );
  };

  if (!benefitsBreakdown) {
    return null;
  }

  const benefitSections: Array<keyof BenefitsBreakdown> = [
    "month",
    "energySupportSocialTariffs",
    "discretionary",
    "lumpSum",
    "homeImprovementsGrants",
    "other",
  ];

  // Calculate the total amount from ALL applied benefits that have amounts
  const appliedMonthlyAmount = benefitSections.reduce((total, section) => {
    const items = benefitsBreakdown[section];
    if (!items) return total;

    return (
      total +
      items
        .filter((benefit) => appliedBenefits.includes(benefit.id))
        .reduce((sum, benefit) => {
          // Check if the benefit has an amount property (MonthlyBenefit type)
          return sum + (isMonthlyBenefit(benefit) ? benefit.amount : 0);
        }, 0)
    );
  }, 0);

  // Calculate remaining unclaimed amount
  const remainingAmount =
    (benefitsSummary?.monthlyBenefitsAmount || 0) - appliedMonthlyAmount;

  return (
    <div className="content-container">
      <div className="flex justify-between items-center">
        {/* Page Title */}
        <h1 className="text-2xl md:text-3xl font-bold">Benefits</h1>
        {/* Edit Details Button */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate("/full-benefits")}
            className="inline-flex items-center"
          >
            Edit
            <img src={editIcon} alt="edit icon" className="w-5 h-5 ml-1" />
          </button>
        </div>
      </div>

      {/* Blue text box */}
      <div className="rounded-20 p-6 bg-primary mt-1.5 text-center md:text-left benefits-summary-box">
        {benefitsSummary?.monthlyBenefitsAmount &&
        benefitsSummary.monthlyBenefitsAmount > 0 ? (
          appliedBenefits.length > 0 ? (
            <p>
              Great work claiming some benefits. Our check indicates you may
              still be eligible for £{remainingAmount.toFixed(2)}. Go ahead and
              see what you may be eligible to claim for.
            </p>
          ) : (
            <>
              <p>
                <b>
                  You may be eligible for up to £
                  {benefitsSummary.monthlyBenefitsAmount.toFixed(2)} per month
                  in benefits.
                </b>
              </p>
              <p className="mt-2">
                You can start applying now and mark each one as applied to keep
                track of your progress.
              </p>
            </>
          )
        ) : (
          <>
            <p>
              Based on what you've told us, you're not eligible for benefits
              right now.
            </p>
            <p>
              Small changes to your income or rent can change this. You can
              update your answers at any time to check again.
            </p>
          </>
        )}
      </div>

      {/* Benefits List */}
      <div className="mt-8">
        {benefitSections.map((section) => {
          const items = benefitsBreakdown[section];
          if (!items || items.length === 0) return null;

          return (
            <div key={section} className="mb-8">
              <p className="font-bold mb-2">{sectionHeaderMapping[section]}</p>
              <div>{items.map((item) => renderBenefitListItem(item))}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BenefitsSummary;
